/**
 * Press releases main view component - based on ReportsView.jsx
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useToast, ToastContainer } from '../common/Toast';
import pressReleaseService from '../../services/pressReleaseService';
import PressReleaseDetailView from './PressReleaseDetailView';

const PressReleasesView = () => {
  const { state } = useAppContext();
  const { toasts, showToast, removeToast } = useToast();
  
  // Local state
  const [pressReleases, setPressReleases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rssModalOpen, setRSSModalOpen] = useState(false);
  const [selectedPressRelease, setSelectedPressRelease] = useState(null);
  const [scrapingStatus, setScrapingStatus] = useState({});

  // Load press releases
  const loadPressReleases = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading press releases...');
      
      const response = await pressReleaseService.getPressReleases({ limit: 100, skip: 0 });
      console.log('📡 API Response:', response);
      
      if (response && response.success) {
        // Access nested data from API response
        const pressReleasesList = response.data?.press_releases || [];
        const total = response.data?.total || 0;
        
        console.log('📰 Press releases found:', pressReleasesList.length, 'Total:', total);
        setPressReleases(pressReleasesList);
        setTotalCount(total);
        
        if (pressReleasesList.length === 0) {
          console.warn('⚠️ No press releases in response data');
        }
      } else {
        console.error('❌ API Response failed:', response);
        setPressReleases([]);
        setTotalCount(0);
        // Show error message to user
        showToast('❌ Failed to load press releases. Please refresh.', 'error');
      }
    } catch (error) {
      console.error('💥 Error loading press releases:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setPressReleases([]);
      setTotalCount(0);
      
      // Show specific error to user
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Error loading press releases: ${errorMsg}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Load press releases on mount
  useEffect(() => {
    loadPressReleases();
  }, [loadPressReleases]);

  // Handle RSS feed management
  const handleAddRSSFeed = useCallback(async (feedData) => {
    try {
      showToast('Adding RSS feed...', 'info');
      
      const response = await pressReleaseService.createRSSFeed(feedData);
      
      if (response && response.success) {
        showToast('✅ RSS feed added successfully!', 'success');
        setRSSModalOpen(false);
        // Optionally refresh feed immediately
        if (response.data?.id) {
          setTimeout(() => {
            handleRefreshFeed(response.data.id);
          }, 1000);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Unknown error';
        console.error('RSS feed creation failed:', errorMsg);
        showToast(`❌ Failed to add RSS feed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error adding RSS feed:', error);
      
      let errorMessage = 'Unknown error';
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        errorMessage = data?.detail || data?.message || `Server error: ${status}`;
      } else if (error.request) {
        errorMessage = 'No response from server - check connection';
      } else {
        errorMessage = error.message || 'Request setup error';
      }
      
      showToast(`❌ RSS feed error: ${errorMessage}`, 'error');
    }
  }, [showToast]);

  // Handle refresh feeds
  const handleRefreshAllFeeds = useCallback(async () => {
    try {
      showToast('Refreshing all RSS feeds...', 'info');
      
      const response = await pressReleaseService.refreshAllFeeds();
      
      if (response && response.success) {
        const data = response.data;
        showToast(
          `✅ Feed refresh complete!\n📊 ${data.successful_feeds} feeds processed\n📰 ${data.total_new_releases} new press releases discovered`, 
          'success'
        );
        loadPressReleases(); // Refresh list
      } else {
        const errorMsg = response?.error || response?.message || 'Feed refresh failed';
        showToast(`❌ ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error refreshing feeds:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Refresh error: ${errorMessage}`, 'error');
    }
  }, [loadPressReleases, showToast]);

  // Handle single feed refresh
  const handleRefreshFeed = useCallback(async (feedId) => {
    try {
      showToast('Refreshing RSS feed...', 'info');
      
      const response = await pressReleaseService.refreshRSSFeed(feedId);
      
      if (response && response.success) {
        const data = response.data;
        showToast(`✅ ${data.message}\n📰 ${data.new_releases} new press releases`, 'success');
        loadPressReleases(); // Refresh list
      } else {
        const errorMsg = response?.error || response?.message || 'Feed refresh failed';
        showToast(`❌ ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error refreshing feed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Refresh error: ${errorMessage}`, 'error');
    }
  }, [loadPressReleases, showToast]);

  // Handle scrape press release content
  const handleScrape = useCallback(async (pressReleaseId) => {
    try {
      setScrapingStatus(prev => ({ ...prev, [pressReleaseId]: true }));
      showToast('Scraping press release content...', 'info');
      
      const response = await pressReleaseService.scrapePressRelease(pressReleaseId);
      
      if (response && response.success) {
        showToast('✅ Press release content scraped successfully!', 'success');
        loadPressReleases(); // Refresh list to show updated status
      } else {
        const errorMsg = response?.error || response?.message || 'Scraping failed';
        showToast(`❌ Scraping failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error scraping press release:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Scraping error: ${errorMessage}`, 'error');
    } finally {
      setScrapingStatus(prev => ({ ...prev, [pressReleaseId]: false }));
    }
  }, [loadPressReleases, showToast]);

  // Handle view press release details
  const handleViewPressRelease = (pressRelease) => {
    setSelectedPressRelease(pressRelease);
  };

  // Handle back from detail view
  const handleBackFromDetail = () => {
    setSelectedPressRelease(null);
    loadPressReleases(); // Refresh list when returning
  };

  // Handle press release update from detail view
  const handlePressReleaseUpdate = (updatedPressRelease) => {
    setPressReleases(prev => prev.map(pr => 
      pr.id === updatedPressRelease.id ? updatedPressRelease : pr
    ));
  };

  // If viewing a specific press release, show detail view
  if (selectedPressRelease) {
    return (
      <PressReleaseDetailView
        pressRelease={selectedPressRelease}
        onBack={handleBackFromDetail}
        onUpdate={handlePressReleaseUpdate}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Press Releases</h1>
            <p className="text-sm text-gray-600">
              Discover and manage press releases from RSS feeds
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setRSSModalOpen(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add RSS Feed</span>
            </button>
            <button
              onClick={handleRefreshAllFeeds}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh Feeds</span>
            </button>
            <button
              onClick={loadPressReleases}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading press releases...</p>
            </div>
          </div>
        ) : pressReleases && pressReleases.length > 0 ? (
          <div className="h-full flex flex-col">
            {/* Press Release List Header */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center text-xs font-medium text-gray-700">
              <div className="w-4 flex-shrink-0 mr-3"></div>
              <div className="w-8 flex-shrink-0 mr-3"></div>
              <div className="flex-1 min-w-0 mr-4">Title & Date</div>
              <div className="w-32 flex-shrink-0 mr-4">Company</div>
              <div className="w-24 flex-shrink-0 mr-4">RSS Source</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Scraped</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Article</div>
              <div className="w-32 text-center flex-shrink-0">Action</div>
            </div>
            
            {/* Press Release Rows */}
            <div className="flex-1 overflow-auto bg-white">
              {pressReleases.map((pressRelease) => (
                <div key={pressRelease.id} className="px-4 py-3 border-b border-gray-200 flex items-center hover:bg-gray-50">
                  {/* Checkbox */}
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300 flex-shrink-0 mr-3" />
                  
                  {/* News Icon */}
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3">
                    <span className="text-xl">📰</span>
                  </div>
                  
                  {/* Title & Date Column */}
                  <div className="flex-1 min-w-0 mr-4">
                    <div 
                      className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer truncate"
                      onClick={() => handleViewPressRelease(pressRelease)}
                    >
                      {pressRelease.title}
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {new Date(pressRelease.publication_date).toLocaleDateString()} • {pressRelease.description?.substring(0, 50)}...
                    </div>
                  </div>
                  
                  {/* Company Column */}
                  <div className="w-32 flex-shrink-0 mr-4">
                    <div className="text-sm text-gray-900 truncate">
                      {pressRelease.company || '-'}
                    </div>
                  </div>
                  
                  {/* RSS Source Column */}
                  <div className="w-24 flex-shrink-0 mr-4">
                    <div className="text-xs text-gray-600 truncate" title={pressRelease.rss_source_name}>
                      {pressRelease.rss_source_name}
                    </div>
                  </div>
                  
                  {/* Status Columns */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    {pressRelease.content_extracted ? (
                      <span className="text-green-600 text-lg" title="Content extracted">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg" title="Not extracted">✗</span>
                    )}
                  </div>
                  
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    {pressRelease.article_drafted ? (
                      <span className="text-green-600 text-lg" title="Article generated">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg" title="No article yet">✗</span>
                    )}
                  </div>
                  
                  {/* Action Button */}
                  <div className="w-32 flex items-center justify-center flex-shrink-0">
                    {!pressRelease.content_extracted ? (
                      <button 
                        onClick={() => handleScrape(pressRelease.id)}
                        disabled={scrapingStatus[pressRelease.id]}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:bg-blue-400 flex items-center space-x-1"
                      >
                        {scrapingStatus[pressRelease.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                            <span>Scraping...</span>
                          </>
                        ) : (
                          <span>Scrape</span>
                        )}
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleViewPressRelease(pressRelease)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Results Summary */}
            <div className="px-4 py-2 bg-white border-t border-gray-200">
              <div className="text-xs text-gray-700">
                Showing {pressReleases.length} of {totalCount} press releases
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <h3 className="text-lg font-medium mb-2">No press releases found</h3>
              <p className="text-center mb-4">Get started by adding your first RSS feed.</p>
              
              <button
                onClick={() => setRSSModalOpen(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                Add Your First RSS Feed
              </button>
              
              <div className="bg-white border border-gray-200 p-4 rounded-lg max-w-md mx-auto mt-6">
                <h4 className="font-medium mb-2">Press Release Workflow:</h4>
                <div className="flex justify-center space-x-4 text-xs">
                  <span>📰 Discover</span>
                  <span>✓ Scrape</span>
                  <span>✓ Generate Article</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RSS Feed Modal */}
      {rssModalOpen && (
        <RSSFeedModal
          isOpen={rssModalOpen}
          onClose={() => setRSSModalOpen(false)}
          onSubmit={handleAddRSSFeed}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

// RSS Feed Modal Component
const RSSFeedModal = ({ isOpen, onClose, onSubmit }) => {
  const [feedName, setFeedName] = useState('');
  const [rssUrl, setRSSUrl] = useState('');
  const [feedDescription, setFeedDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedName || !rssUrl) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        feed_name: feedName,
        rss_url: rssUrl,
        feed_description: feedDescription
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Add RSS Feed</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={submitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Feed Name *
            </label>
            <input
              type="text"
              value={feedName}
              onChange={(e) => setFeedName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g., EIN Presswire Real Estate"
              required
              disabled={submitting}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSS URL *
            </label>
            <input
              type="url"
              value={rssUrl}
              onChange={(e) => setRSSUrl(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="https://example.com/rss"
              required
              disabled={submitting}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={feedDescription}
              onChange={(e) => setFeedDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Brief description of this RSS feed"
              disabled={submitting}
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors text-sm flex items-center justify-center"
              disabled={submitting || !feedName || !rssUrl}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                'Add RSS Feed'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PressReleasesView;